import {Field, Formik, FormikHelpers as FormikActions} from 'formik';
import React from 'react';
import {Button} from '~/components/Button';
import {
    EditorField,
    Form,
    FormColumn,
    FormControls,
    FormRow,
    FormTitle,
    InputField,
    IntentText,
    SelectField
} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';

import {SelectSellerField} from '~/modules/Clients/components/SelectSeller';
import {MultiSelectAgencyField} from '~/modules/Clients/components/SelectAgency';
import {
    IBlocking,
    BlockingSchema,
    BlockingType,
    getBlockingTypesLabel,
    BlockingAgencyMode
} from '~/modules/Clients/models/Blocking';

interface IBlockingForm {
    blocking?: IBlocking;
    lockSeller?: boolean;
    showAgencyField?: boolean;
    onSubmit?: (
        values: IBlocking,
        formikActions: FormikActions<IBlocking>,
        close: IModalComponentClose
    ) => void;
}

function arrToField(val: string[] | string | undefined): string {
    if (Array.isArray(val)) {
        return val.join('\n');
    }
    if (typeof val === 'string') {
        return val;
    }
    return '';
}

function fieldToArray(val: string | string[]): string[] {
    if (Array.isArray(val)) {
        return val;
    }
    if (typeof val === 'string') {
        return val
            .split(/[\n,]/)
            .map(s => s.trim())
            .filter(s => s.length > 0);
    }
    return [];
}

export const BlockingForm: IModalComponent<IBlockingForm> = props =>
    <Formik
        initialValues={{
            ...props.blocking,

            agencyIds: Array.isArray(props.blocking?.agencyIds)
                ? props.blocking.agencyIds
                : [],

            agencyMode: (() => {
                const ids = Array.isArray(props.blocking?.agencyIds)
                    ? props.blocking.agencyIds
                    : [];
                const inc = props.blocking?.includesAgencies;
                if (!ids || ids.length === 0) {
                    return BlockingAgencyMode.ALL;
                }
                if (inc === false) {
                    return BlockingAgencyMode.EXCLUDE;
                }
                return BlockingAgencyMode.INCLUDE;
            })(),

            bundles: arrToField(props.blocking?.bundles),
            blockingValues: arrToField(props.blocking?.blockingValues)
        }}
        validationSchema={BlockingSchema}
        onSubmit={(values, actions) => {
            const ids = Array.isArray(values.agencyIds)
                ? values.agencyIds.filter(id => id !== null && id !== undefined)
                : [];

            let includesAgencies: boolean;
            let agencyIds: number[];

            if (values.agencyMode === BlockingAgencyMode.ALL) {
                includesAgencies = true;
                agencyIds = [];
            } else if (values.agencyMode === BlockingAgencyMode.INCLUDE) {
                includesAgencies = true;
                agencyIds = ids;
            } else {
                includesAgencies = false;
                agencyIds = ids;
            }

            const bundlesArr = fieldToArray(values.bundles as any);
            const blockingValuesArr = fieldToArray(values.blockingValues as any);

            const nextBlocking: IBlocking = {
                ...values,
                agencyIds,
                includesAgencies,
                bundles: bundlesArr,
                blockingValues: blockingValuesArr
            };

            props.onSubmit(nextBlocking, actions, props.close);
        }}
    >
        {formik => {
            const {values: blocking, status, isSubmitting} = formik;

            return (
                <Form>
                    <FormTitle>{blocking.id ? 'Edit Blocking' : 'Create Blocking'}</FormTitle>

                    {status && (
                        <IntentText intent={Intent.DANGER}>
                            {status}
                        </IntentText>
                    )}

                    <FormRow noBorder>
                        <FormColumn>
                            <Field
                                name='sellerId'
                                label='Seller'
                                options={{
                                    placeholder: 'Seller',
                                    searchable: true,
                                    disabled: props.lockSeller || isSubmitting
                                }}
                                component={SelectSellerField}
                            />

                            <Field
                                name='reason'
                                label='Reason'
                                options={{
                                    disabled: isSubmitting
                                }}
                                component={InputField}
                            />

                            <Field
                                name='blockingType'
                                label='Type'
                                options={{
                                    placeholder: 'Type',
                                    disabled: isSubmitting,
                                    items: [
                                        {label: getBlockingTypesLabel(BlockingType.CAT), value: BlockingType.CAT},
                                        {label: getBlockingTypesLabel(BlockingType.ADV), value: BlockingType.ADV},
                                        {label: getBlockingTypesLabel(BlockingType.APP), value: BlockingType.APP},
                                        {label: getBlockingTypesLabel(BlockingType.CID), value: BlockingType.CID}
                                    ]
                                }}
                                component={SelectField}
                            />

                            {props.showAgencyField && (
                                <>
                                    <Field
                                        name='agencyMode'
                                        label='Agencies'
                                        options={{
                                            disabled: isSubmitting,
                                            items: [
                                                { label: 'Use all agencies', value: BlockingAgencyMode.ALL },
                                                { label: 'Use only selected agencies', value: BlockingAgencyMode.INCLUDE },
                                                { label: 'Exclude selected agencies', value: BlockingAgencyMode.EXCLUDE }
                                            ],
                                            placeholder: 'Agencies'
                                        }}
                                        component={SelectField}
                                    />

                                    {(blocking.agencyMode === BlockingAgencyMode.INCLUDE ||
                                      blocking.agencyMode === BlockingAgencyMode.EXCLUDE) && (
                                        <Field
                                            name='agencyIds'
                                            label=' '
                                            options={{
                                                disabled: isSubmitting,
                                                searchable: true
                                            }}
                                            component={MultiSelectAgencyField}
                                        />
                                    )}
                                </>
                            )}

                            <Field
                                name='bundles'
                                label='Apps'
                                options={{
                                    mode: 'text',
                                    canCopy: true,
                                    disabled: isSubmitting,
                                    minLines: 4,
                                    maxLines: 20
                                }}
                                component={EditorField}
                            />

                            <Field
                                name='blockingValues'
                                label='Values'
                                options={{
                                    mode: 'text',
                                    canCopy: true,
                                    disabled: isSubmitting,
                                    minLines: 4,
                                    maxLines: 20
                                }}
                                component={EditorField}
                            />
                        </FormColumn>
                    </FormRow>

                    <FormControls>
                        <Button
                            disabled={isSubmitting}
                            onClick={props.close}
                        >
                            Cancel
                        </Button>

                        <Button
                            type='submit'
                            disabled={isSubmitting}
                            intent={Intent.PRIMARY}
                            rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                        >
                            Save
                        </Button>
                    </FormControls>
                </Form>
            );
        }}
    </Formik>;
